import React from 'react';

import { RangeRule, RequiredRule } from 'devextreme-react/form';
import { DropDownOptions } from 'devextreme-react/select-box';
import Validator from 'devextreme-react/validator';

import { AppCol, AppRow } from 'components/appComponents';
import AppFiledBox from 'components/appComponents/app-field-box';
import { Button, CheckBox, NumberBox, SelectBox, TagBox, TextArea, TextBox } from 'devextreme-react';
import { PUMP_ITEMS, PUMP_ITEMS_INTEGER } from 'pages/RequestTask/Task.constants';

import { MethodOfReception } from 'bus/methodOfReception/types';
import { RequestPriceResponse } from 'bus/request/types';

type Props = {
  form: MethodOfReception;
  methodTypes: any[];
  storeTransport: any;
  storeEmployee: any;
  totalValue: number;
  handleChangeForm: (newValue: Partial<MethodOfReception>) => void;
  clearButton: (name: keyof RequestPriceResponse) => void;
  pumpPrice: RequestPriceResponse | undefined;
};

export const MethodOfReceptionForm: React.FC<Props> = ({
  form,
  methodTypes,
  storeTransport,
  storeEmployee,
  totalValue,
  pumpPrice,
  handleChangeForm,
  clearButton,
}) => {
  return (
    <AppRow>
      <AppCol col={7}>
        <AppFiledBox label="Метод приёма" labelTop requere>
          <SelectBox
            stylingMode="underlined"
            items={methodTypes}
            displayExpr="name"
            valueExpr="id"
            value={form.type}
            searchEnabled
            onItemClick={(e) => {
              handleChangeForm({ type: e.itemData.id });
            }}
          >
            <DropDownOptions title="Выберите Метод приёма" closeOnOutsideClick={true} showTitle={false} />
            <Validator>
              <RequiredRule message="Выберите Метод приёма" />
            </Validator>
          </SelectBox>
        </AppFiledBox>

        {form.type === 1 && (
          <AppFiledBox label="Доплата за рюмку" labelTop requere>
            <NumberBox
              stylingMode="underlined"
              placeholder="0"
              mode="text"
              value={form.glassConcretePrice ?? 0}
              min={0}
              onValueChange={(e) => {
                handleChangeForm({ glassConcretePrice: e || 0 });
              }}
            >
              <Validator>
                <RequiredRule message="Заполните поле доплату за рюмку" />
                <RangeRule min={0} message="Поле должно быть больше 0" />
              </Validator>
            </NumberBox>
          </AppFiledBox>
        )}

        {form.type === 3 && (
          <>
            <AppFiledBox label="Транспорт" labelTop>
              <SelectBox
                stylingMode="underlined"
                dataSource={storeTransport}
                displayExpr="fullName"
                valueExpr="id"
                value={form.transportId}
                searchEnabled
                onItemClick={(e) => {
                  handleChangeForm({ transportId: e.itemData.id });
                }}
              >
                <DropDownOptions title="Выберите Транспорт" closeOnOutsideClick={true} showTitle={false} />
              </SelectBox>
            </AppFiledBox>

            <AppFiledBox label="Водитель" labelTop>
              <TagBox
                stylingMode="underlined"
                dataSource={storeEmployee}
                displayExpr="fullName"
                valueExpr="id"
                showSelectionControls
                showClearButton
                searchEnabled
                value={form.driverIds || []}
                onValueChanged={(e) => {
                  const nextIds = (e.value as number[]) || [];
                  handleChangeForm({ driverIds: nextIds });
                }}
                maxDisplayedTags={2}
              >
                <DropDownOptions title="Выберите водителя" closeOnOutsideClick showTitle />
              </TagBox>
            </AppFiledBox>

            <AppFiledBox label="Телефон" labelTop>
              <TextBox
                stylingMode="underlined"
                placeholder="Введите телефон"
                mode="text"
                value={form.pumpPhone}
                onValueChange={(e) => {
                  handleChangeForm({ pumpPhone: e });
                }}
                mask="+7 (000) 000-00-00"
              />
            </AppFiledBox>
          </>
        )}

        <AppFiledBox label="Комментарий" labelTop>
          <TextArea
            stylingMode="underlined"
            placeholder="Введите комментарий"
            mode="text"
            value={form.comment || ''}
            onChange={(e: any) => {
              handleChangeForm({ comment: e.event.currentTarget.value });
            }}
          />
        </AppFiledBox>
      </AppCol>
      <AppCol col={5}>
        {form.type === 3 && (
          <>
            <AppFiledBox label="Кол-во кубов" labelTop>
              <AppRow>
                <AppCol col={11}>
                  <NumberBox
                    stylingMode="underlined"
                    placeholder="0"
                    mode="text"
                    value={form.pumpQuantity ?? undefined}
                    min={0}
                    onValueChange={(e) => {
                      handleChangeForm({
                        pumpQuantity: e || 0,
                        isFilledManually: true,
                      });
                    }}
                  >
                    <Validator>
                      <RangeRule min={0} message="Поле должно быть больше 0" />
                    </Validator>
                  </NumberBox>
                </AppCol>
                <AppCol col={1}>
                  <Button
                    icon="revert"
                    onClick={() => {
                      handleChangeForm({ isFilledManually: false });
                    }}
                  />
                </AppCol>
              </AppRow>
            </AppFiledBox>

            <AppFiledBox label="Цена за куб" labelTop>
              <AppRow>
                <AppCol col={11}>
                  <NumberBox
                    stylingMode="underlined"
                    placeholder="0"
                    mode="text"
                    disabled={form.transportId! < 0}
                    value={form.pumpPrice ?? undefined}
                    onValueChange={(e) => {
                      handleChangeForm({
                        pumpPrice: e || 0,
                        pumpPriceFilledManually: true,
                      });
                    }}
                  >
                    <Validator>
                      <RequiredRule message="Заполните поле Цена за куб" />
                    </Validator>
                  </NumberBox>
                </AppCol>
                <AppCol col={1}>
                  <Button
                    icon="revert"
                    onClick={() => {
                      clearButton('price');
                      handleChangeForm({ pumpPriceFilledManually: false });
                    }}
                  />
                </AppCol>
              </AppRow>
            </AppFiledBox>

            <AppFiledBox label="Минимальная цена" labelTop>
              <AppRow>
                <AppCol col={11}>
                  <NumberBox
                    stylingMode="underlined"
                    placeholder="0"
                    format="###0.##"
                    value={form.pumpPriceMinimal ?? undefined}
                    onValueChange={(e) => {
                      handleChangeForm({
                        pumpPriceMinimal: e || 0,
                        pumpPriceMinimalFilledManually: true,
                      });
                    }}
                  />
                </AppCol>
                <AppCol col={1}>
                  <Button
                    icon="revert"
                    onClick={() => {
                      clearButton('pumpPriceMinimal');
                      handleChangeForm({ pumpPriceMinimalFilledManually: false });
                    }}
                  />
                </AppCol>
              </AppRow>
            </AppFiledBox>

            <AppFiledBox label="Подача" labelTop>
              <AppRow>
                <AppCol col={11}>
                  <NumberBox
                    stylingMode="underlined"
                    placeholder="0"
                    format="###0.##"
                    value={form.pumpInnings ?? undefined}
                    onValueChange={(e) => {
                      handleChangeForm({
                        pumpInnings: e || 0,
                        pumpInningsFilledManually: true,
                      });
                    }}
                  />
                </AppCol>
                <AppCol col={1}>
                  <Button
                    icon="revert"
                    onClick={() => {
                      clearButton('pumpInnings');
                      handleChangeForm({ pumpInningsFilledManually: false });
                    }}
                  />
                </AppCol>
              </AppRow>
            </AppFiledBox>

            <AppFiledBox label="Гаситель" labelTop>
              <AppRow>
                <AppCol col={10}>
                  <NumberBox
                    stylingMode="underlined"
                    placeholder="0"
                    format="###0.##"
                    value={form.pumpQuencher ?? undefined}
                    onValueChange={(e) => {
                      handleChangeForm({
                        pumpQuencher: e || 0,
                        pumpQuencherFilledManually: true,
                      });
                    }}
                  />
                </AppCol>
                <AppCol col={1}>
                  <CheckBox
                    value={form.useQuencher}
                    onValueChange={(e) => {
                      handleChangeForm({ useQuencher: Boolean(e) });

                      if (!e) {
                        handleChangeForm({ pumpQuencher: 0 });
                        handleChangeForm({ pumpQuencherFilledManually: false });
                      } else {
                        handleChangeForm({ pumpQuencher: pumpPrice?.pumpQuencher || 0 });
                      }
                    }}
                    className="pt-1"
                  />
                </AppCol>
                <AppCol col={1}>
                  <Button
                    icon="revert"
                    onClick={() => {
                      clearButton('pumpQuencher');
                      handleChangeForm({ pumpQuencherFilledManually: false });
                    }}
                  />
                </AppCol>
              </AppRow>
            </AppFiledBox>

            <AppFiledBox label="Переезд" labelTop>
              <AppRow>
                <AppCol col={8}>
                  <AppRow>
                    <AppCol col={10}>
                      <NumberBox
                        stylingMode="underlined"
                        placeholder="0"
                        format="###0.##"
                        value={form.pumpMoving ?? undefined}
                        onValueChange={(e) => {
                          handleChangeForm({
                            pumpMoving: e || 0,
                            pumpMovingFilledManually: true,
                          });
                        }}
                      />
                    </AppCol>
                    <AppCol col={2}>
                      <Button
                        icon="revert"
                        onClick={() => {
                          clearButton('pumpMoving');
                          handleChangeForm({ pumpMovingFilledManually: false });
                        }}
                      />
                    </AppCol>
                  </AppRow>
                </AppCol>
                <AppCol col={4}>
                  <SelectBox
                    stylingMode="underlined"
                    placeholder=""
                    items={PUMP_ITEMS_INTEGER}
                    value={form.pumpMovingCount ?? 0}
                    onItemClick={(e) => {
                      handleChangeForm({ pumpMovingCount: e.itemData || 0 });

                      if (e.itemData === 0) {
                        handleChangeForm({ pumpMoving: 0 });
                        handleChangeForm({ pumpMovingFilledManually: false });
                      } else {
                        handleChangeForm({ pumpMoving: pumpPrice?.pumpMoving || 0 });
                      }
                    }}
                  >
                    <DropDownOptions closeOnOutsideClick={true} showTitle={false} />
                  </SelectBox>
                </AppCol>
              </AppRow>
            </AppFiledBox>

            <AppFiledBox label="Трубы" labelTop>
              <AppRow>
                <AppCol col={8}>
                  <AppRow>
                    <AppCol col={10}>
                      <NumberBox
                        stylingMode="underlined"
                        placeholder="0"
                        format="###0.##"
                        value={form.pumpPipes ?? 0}
                        onValueChange={(e) => {
                          handleChangeForm({
                            pumpPipes: e || 0,
                            pumpPipesFilledManually: true,
                          });
                        }}
                      />
                    </AppCol>
                    <AppCol col={2}>
                      <Button
                        icon="revert"
                        onClick={() => {
                          clearButton('pumpPipes');
                          handleChangeForm({ pumpPipesFilledManually: false });
                        }}
                      />
                    </AppCol>
                  </AppRow>
                </AppCol>
                <AppCol col={4}>
                  <SelectBox
                    stylingMode="underlined"
                    placeholder=""
                    items={PUMP_ITEMS_INTEGER}
                    value={form.pumpPipesCount ?? 0}
                    onItemClick={(e) => {
                      handleChangeForm({ pumpPipesCount: e.itemData || 0 });

                      if (e.itemData === 0) {
                        handleChangeForm({ pumpPipes: 0 });
                        handleChangeForm({ pumpPipesFilledManually: false });
                      } else {
                        handleChangeForm({ pumpPipes: pumpPrice?.pumpPipes || 0 });
                      }
                    }}
                  >
                    <DropDownOptions closeOnOutsideClick={true} showTitle={false} />
                  </SelectBox>
                </AppCol>
              </AppRow>
            </AppFiledBox>

            <AppFiledBox label="Простой" labelTop>
              <AppRow>
                <AppCol col={8}>
                  <AppRow>
                    <AppCol col={10}>
                      <NumberBox
                        stylingMode="underlined"
                        placeholder="0"
                        format="###0.##"
                        value={form.downtime ?? undefined}
                        onValueChange={(e) => {
                          handleChangeForm({
                            downtime: e || 0,
                            downtimeFilledManually: true,
                          });
                        }}
                      />
                    </AppCol>
                    <AppCol col={2}>
                      <Button
                        icon="revert"
                        onClick={() => {
                          clearButton('downtime');
                          handleChangeForm({ downtimeFilledManually: false });
                        }}
                      />
                    </AppCol>
                  </AppRow>
                </AppCol>
                <AppCol col={4}>
                  <SelectBox
                    stylingMode="underlined"
                    placeholder=""
                    items={PUMP_ITEMS}
                    value={form.downtimeHours ?? 0}
                    onItemClick={(e) => {
                      handleChangeForm({ downtimeHours: e.itemData || 0 });

                      if (e.itemData === 0) {
                        handleChangeForm({ downtime: 0 });
                        handleChangeForm({ downtimeFilledManually: false });
                      } else {
                        handleChangeForm({ downtime: pumpPrice?.downtime || 0 });
                      }
                    }}
                  >
                    <DropDownOptions closeOnOutsideClick={true} showTitle={false} />
                  </SelectBox>
                </AppCol>
              </AppRow>
            </AppFiledBox>
            <AppRow>
              <AppCol col={12}>
                <AppFiledBox label="Кол-во километров" labelTop>
                  <NumberBox
                    stylingMode="underlined"
                    placeholder="0"
                    format="###0.##"
                    mode="text"
                    value={form.distance}
                    min={0}
                    onValueChanged={(e) => {
                      handleChangeForm({
                        distance: e.value || 0,
                      });
                    }}
                  />
                </AppFiledBox>
              </AppCol>
            </AppRow>
            <div className="mt-2 mb-2 pl-2" style={{ fontWeight: 'bold', fontSize: 16 }}>
              Итого: {Number(totalValue || 0).toFixed(2)}
            </div>
          </>
        )}
      </AppCol>
    </AppRow>
  );
};
